<template>
  <div :class="{ 'h-full w-full flex flex-col': isPanelOpen }">
    <!-- Floating Button - Right Side, Lower (only show when panel is closed) -->
    <button v-if="!isPanelOpen" @click="togglePanel" class="ai-floating-btn fixed right-5 bottom-5 w-8 h-8 c-bg-brand-dark border-none rounded-full text-white text-sm cursor-pointer shadow-lg c-z-1000 flex items-center justify-center transition-all duration-300 c-hover-bg-brand-darker hover:scale-105 hover:shadow-xl">
      <i class="fas fa-robot"></i>
      <span class="ai-floating-tooltip bg-gray-700 text-white py-2 px-3 rounded-lg text-xs font-medium whitespace-nowrap shadow-md opacity-0 pointer-events-none transition-opacity duration-200">AI Assistant</span>
    </button>

    <!-- AI Panel - Right Side (when open) -->
    <div v-if="isPanelOpen" class="h-full w-full bg-white border-l border-gray-300 flex flex-col overflow-hidden relative">
      <!-- Panel Header -->
      <div class="flex items-start justify-between px-6 pt-3.5 pb-4 border-b border-gray-200 bg-white flex-shrink-0 z-10">
        <div class="flex items-start gap-3 pt-0.5">
          <i class="fas fa-robot c-text-brand c-bg-brand-lightest w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-base"></i>
          <div class="flex flex-col gap-0.5">
            <h3 class="m-0 text-base font-semibold text-gray-800 tracking-wide leading-tight">AI Assistant</h3>
            <p class="ai-subtitle m-0 c-text-tiny font-normal text-gray-400 leading-tight truncate cursor-default" :title="currentChatTitleFull">
              {{ currentChatTitle }}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <!-- History Button -->
          <button
            @click="toggleHistoryPanel"
            class="c-bg-brand-lightest border-none c-text-brand text-xs cursor-pointer p-1.5 flex items-center justify-center rounded-lg transition-all duration-200 w-7 h-7 c-hover-bg-brand hover:text-white hover:scale-105"
            title="Chat History"
          >
            <i class="fas fa-history"></i>
          </button>
          <!-- New Chat Button - Only show when there are messages -->
          <button
            v-if="messages.length > 0"
            @click="startNewChat"
            class="c-bg-brand-lightest border-none c-text-brand text-xs cursor-pointer p-1.5 flex items-center justify-center rounded-lg transition-all duration-200 w-7 h-7 c-hover-bg-brand hover:text-white hover:scale-105"
            title="Start New Chat"
          >
            <i class="fas fa-plus"></i>
          </button>
          <button @click="closePanel" class="bg-gray-100 border-none text-gray-500 text-xs cursor-pointer p-1.5 flex items-center justify-center rounded-lg transition-all duration-200 w-7 h-7 hover:bg-red-50 hover:text-red-600 hover:scale-105" title="Close">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Panel Body - Scrollable -->
      <div class="ai-panel-body flex-1 overflow-y-auto overflow-x-hidden p-0 min-h-0 relative flex flex-col scroll-smooth" ref="messageContainer">
        <!-- Empty State -->
        <div v-if="messages.length === 0" class="flex flex-col items-center justify-center absolute inset-0 w-full h-full text-gray-400 py-10 px-5">
          <i class="fas fa-robot text-5xl text-gray-300 mb-4"></i>
          <p class="text-sm text-gray-400 m-0">Start a conversation with AI Assistant</p>
        </div>

        <!-- Messages List -->
        <div v-else class="flex flex-col gap-4 p-5">
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="ai-message flex w-full"
            :class="message.type"
          >
            <!-- User Message -->
            <div v-if="message.type === 'user'" class="flex justify-end w-full">
              <div class="ai-user-bubble c-user-bubble-gradient text-white py-3 px-4 break-words shadow-sm whitespace-pre-wrap">
                <p class="m-0 text-xs leading-relaxed">{{ message.text }}</p>
              </div>
            </div>

            <!-- AI Message -->
            <div v-else-if="message.type === 'ai'" class="flex justify-start w-full">
              <div
                class="ai-message-bubble-ai pt-3 px-4 pb-5 break-words relative shadow-sm overflow-hidden"
                :class="message.isError ? 'ai-error-bubble' : 'bg-gray-100 text-gray-800 border border-gray-300'"
              >
                <!-- New Component Creation Button -->
                <!-- Show button only if: NOT from history AND isNewComponent is true (or undefined for new responses) AND is latest actionable message -->
                <div
                  v-if="
                    message.codeMetadata &&
                    message.codeMetadata.type === 'component' &&
                    message.codeMetadata.component &&
                    !message.codeMetadata.isHistory &&
                    (message.codeMetadata.isNewComponent === true ||
                      message.codeMetadata.isNewComponent === undefined) &&
                    index === latestActionableMessageIndex
                  "
                  class="flex justify-end mb-2 pb-1.5 border-b border-gray-300"
                >
                  <button
                    @click="handleCreateComponent(message.codeMetadata, index)"
                    class="ai-insert-btn"
                    :disabled="
                      message.componentCreated || message.componentCreating
                    "
                  >
                    <i
                      class="fas"
                      :class="
                        message.componentCreated
                          ? 'fa-check'
                          : message.componentCreating
                            ? 'fa-spinner fa-spin'
                            : 'fa-plus-square'
                      "
                    ></i>
                    <span v-if="message.componentCreated"
                      >Component Created</span
                    >
                    <span v-else-if="message.componentCreating"
                      >Creating Component...</span
                    >
                    <span v-else
                      >Create Component:
                      {{ message.codeMetadata.component.name }}</span
                    >
                  </button>
                </div>

                <!-- Multi-Component Feature Insert Buttons (for component creation) -->
                <div
                  v-if="
                    message.codeMetadata &&
                    message.codeMetadata.type === 'component' &&
                    message.codeMetadata.components &&
                    message.componentCreated
                  "
                  class="flex flex-wrap gap-2 mb-3 pb-2"
                >
                  <div
                    v-for="(component, compIndex) in message.codeMetadata
                      .components"
                    :key="compIndex"
                    class="flex-none max-w-full"
                  >
                    <button
                      v-if="shouldShowInsertButton(component)"
                      @click="
                        handleInsertFeatureComponent(
                          message.codeMetadata,
                          compIndex,
                        )
                      "
                      class="ai-insert-btn ai-feature-btn"
                      :disabled="component.inserted || component.inserting"
                    >
                      <i
                        class="fas"
                        :class="
                          component.inserted
                            ? 'fa-check'
                            : component.action === 'replace'
                              ? 'fa-sync-alt'
                              : 'fa-plus-circle'
                        "
                      ></i>
                      <span
                        v-if="component.inserted && component.type === 'css'"
                        >CSS ✓</span
                      >
                      <span v-else-if="component.inserted"
                        >{{ component.name }} ✓</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'method'
                        "
                        >Replace method: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'computed'
                        "
                        >Replace computed: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'watch'
                        "
                        >Replace watch: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'data'
                        "
                        >Replace data: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.type === 'css' &&
                          component.action === 'append'
                        "
                        >Insert CSS</span
                      >
                      <span
                        v-else-if="
                          component.type === 'css' &&
                          component.action === 'replace_all'
                        "
                        >Replace All CSS</span
                      >
                      <span v-else-if="component.type === 'css'"
                        >Insert CSS</span
                      >
                      <span v-else-if="component.type === 'data'"
                        >Insert data: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'method'"
                        >Insert method: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'computed'"
                        >Insert computed: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'watch'"
                        >Insert watch: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'template'"
                        >Replace Template</span
                      >
                      <span v-else
                        >Insert {{ component.type }}: {{ component.name }}</span
                      >
                    </button>
                  </div>
                </div>

                <!-- Single Component Insert Button -->
                <!-- Show button only if NOT from history AND is latest actionable message -->
                <div
                  v-if="
                    message.codeMetadata &&
                    !message.codeMetadata.components &&
                    !message.codeMetadata.isHistory &&
                    shouldShowInsertButton(message.codeMetadata) &&
                    index === latestActionableMessageIndex
                  "
                  class="flex justify-end mb-2 pb-1.5 border-b border-gray-300"
                >
                  <button
                    @click="handleInsertCode(message.codeMetadata, index)"
                    class="ai-insert-btn"
                    :disabled="message.inserted || message.inserting"
                  >
                    <i
                      class="fas"
                      :class="
                        message.inserted
                          ? 'fa-check'
                          : message.codeMetadata.action === 'replace'
                            ? 'fa-sync-alt'
                            : 'fa-plus-circle'
                      "
                    ></i>
                    <span v-if="message.inserted">{{
                      message.codeMetadata.action === "replace" ||
                      message.codeMetadata.action === "replace_all"
                        ? "Replaced"
                        : "Updated"
                    }}</span>
                    <span v-else-if="message.codeMetadata.type === 'template'"
                      >Replace Template</span
                    >
                    <span v-else-if="message.codeMetadata.action === 'replace'"
                      >Replace Code</span
                    >
                    <span
                      v-else-if="
                        message.codeMetadata.type === 'css' &&
                        message.codeMetadata.action === 'append'
                      "
                      >Insert CSS</span
                    >
                    <span
                      v-else-if="
                        message.codeMetadata.type === 'css' &&
                        message.codeMetadata.action === 'replace_all'
                      "
                      >Replace All CSS</span
                    >
                    <span v-else-if="message.codeMetadata.type === 'css'"
                      >Insert CSS</span
                    >
                    <span v-else-if="message.codeMetadata.type === 'data'"
                      >Insert Data</span
                    >
                    <span v-else>Insert Code</span>
                  </button>
                </div>

                <!-- Multi-Component Feature Insert Buttons -->
                <!-- Show buttons only if NOT from history AND is latest actionable message -->
                <div
                  v-if="
                    message.codeMetadata &&
                    message.codeMetadata.type === 'feature' &&
                    message.codeMetadata.components &&
                    !message.codeMetadata.isHistory &&
                    index === latestActionableMessageIndex
                  "
                  class="flex flex-wrap gap-2 mb-3 pb-2"
                >
                  <div
                    v-for="(component, compIndex) in message.codeMetadata
                      .components"
                    :key="compIndex"
                    class="flex-none max-w-full"
                  >
                    <button
                      v-if="shouldShowInsertButton(component)"
                      @click="
                        handleInsertFeatureComponent(
                          message.codeMetadata,
                          compIndex,
                        )
                      "
                      class="ai-insert-btn ai-feature-btn"
                      :disabled="component.inserted || component.inserting"
                    >
                      <i
                        class="fas"
                        :class="
                          component.inserted
                            ? 'fa-check'
                            : component.action === 'replace'
                              ? 'fa-sync-alt'
                              : 'fa-plus-circle'
                        "
                      ></i>
                      <span
                        v-if="component.inserted && component.type === 'css'"
                        >CSS ✓</span
                      >
                      <span v-else-if="component.inserted"
                        >{{ component.name }} ✓</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'method'
                        "
                        >Replace method: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'computed'
                        "
                        >Replace computed: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'watch'
                        "
                        >Replace watch: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.action === 'replace' &&
                          component.type === 'data'
                        "
                        >Replace data: {{ component.name }}</span
                      >
                      <span
                        v-else-if="
                          component.type === 'css' &&
                          component.action === 'append'
                        "
                        >Insert CSS</span
                      >
                      <span
                        v-else-if="
                          component.type === 'css' &&
                          component.action === 'replace_all'
                        "
                        >Replace All CSS</span
                      >
                      <span v-else-if="component.type === 'css'"
                        >Insert CSS</span
                      >
                      <span v-else-if="component.type === 'data'"
                        >Insert data: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'method'"
                        >Insert method: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'computed'"
                        >Insert computed: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'watch'"
                        >Insert watch: {{ component.name }}</span
                      >
                      <span v-else-if="component.type === 'template'"
                        >Replace Template</span
                      >
                      <span v-else
                        >Insert {{ component.type }}: {{ component.name }}</span
                      >
                    </button>
                  </div>
                </div>

                <!-- Revert All Button (shown when multiple previous states exist) -->
                <!-- Show after all insert buttons, outside the feature/components loops -->
                <div
                  v-if="
                    hasMultiplePreviousStates && hasMultiplePreviousStates()
                  "
                  class="flex justify-end mt-2 mb-3 pt-2 border-t border-gray-300"
                >
                  <button
                    @click="handleRevertAll"
                    class="ai-insert-btn ai-revert-btn"
                    :disabled="isReverting && isReverting(null, null)"
                  >
                    <i class="fas fa-undo"></i>
                    <span>Revert All Changes</span>
                  </button>
                </div>

                <div class="ai-message-text m-0 text-xs leading-relaxed break-words">
                  <div
                    v-if="message.useMarkdown && message.text"
                    class="markdown-body text-xs leading-relaxed"
                  >
                    <div
                      class="markdown-content-wrapper relative"
                      :ref="'markdownWrapper_' + index"
                    >
                      <VueMarkdownRender :source="message.text || ''" />
                    </div>
                  </div>
                  <div v-else class="whitespace-pre-wrap break-words">
                    {{ message.text }}
                  </div>
                </div>
                <button
                  @click.stop="copyToClipboard(message.text, index)"
                  class="ai-copy-button absolute bottom-0.5 right-2 border-none rounded h-5 px-1 flex items-center justify-center cursor-pointer text-gray-500 c-text-xxs transition-all duration-200 z-10"
                  :class="{ 'ai-copy-copied': copiedMessageIndex === index }"
                  :title="
                    copiedMessageIndex === index ? 'Copied!' : 'Copy Message'
                  "
                >
                  <span v-if="copiedMessageIndex === index" class="ai-copy-text font-medium text-green-500 whitespace-nowrap"
                    >Copied</span
                  >
                  <i v-else class="fas fa-copy"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Loading Indicator -->
          <div v-if="isGenerating" class="flex w-full mt-2">
            <div class="flex justify-start w-full">
              <div
                class="ai-message-bubble-ai c-bg-loading text-gray-800 p-4 break-words relative border border-gray-300 shadow-sm overflow-hidden"
              >
                <div class="ai-typing-indicator flex gap-1 items-center mb-2">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <p class="m-0 text-xs text-gray-500 italic">
                  {{
                    loadingThreadId
                      ? "Loading chat history..."
                      : "AI is thinking..."
                  }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Panel Footer with Input at Bottom -->
      <div class="ai-panel-footer px-6 pt-2 pb-6 border-t border-gray-300 bg-white flex-shrink-0 z-10">
        <div class="flex flex-col">
          <div class="ai-input-wrapper relative w-full border border-gray-300 rounded-xl bg-white flex flex-col overflow-hidden transition-all duration-200">
            <textarea
              v-model="userPrompt"
              ref="promptTextarea"
              class="ai-prompt-textarea w-full text-sm border-none bg-transparent text-gray-800 resize-none outline-none transition-all duration-200 overflow-y-auto leading-relaxed placeholder-gray-400"
              placeholder="Ask me anything about Vue.js or your component..."
              rows="3"
              @keydown="handleKeyDown"
            ></textarea>

            <!-- Send Button -->
            <button
              @click="handleGenerate"
              class="ai-send-btn absolute right-3 bottom-3 w-8 h-8 text-white border-none rounded-lg cursor-pointer flex items-center justify-center text-sm flex-shrink-0 z-10 transition-all duration-200"
              :disabled="!userPrompt.trim() || isGenerating"
              :title="isGenerating ? 'Generating...' : 'Send'"
            >
              <i v-if="isGenerating" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-paper-plane"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- History Dropdown (positioned below history button) -->
    <div
      v-if="showHistoryPanel"
      class="fixed inset-0 bg-transparent c-z-99998"
      @click="closeHistoryPanel"
    ></div>
    <div v-if="showHistoryPanel" class="ai-history-dropdown absolute top-14 right-2.5 bg-white rounded-lg border border-gray-300 shadow-2xl c-z-99999 flex flex-col overflow-hidden" @click.stop>
      <!-- Search Box -->
      <div class="px-2.5 py-1.5 border-b border-gray-200">
        <input
          type="text"
          v-model="historySearchQuery"
          placeholder="Search..."
          class="w-full py-1.5 px-2.5 bg-gray-100 border border-gray-300 rounded text-gray-800 text-xs outline-none transition-colors duration-200 placeholder-gray-400 c-focus-border-brand focus:bg-white"
        />
      </div>

      <div class="flex-1 overflow-y-auto max-h-80">
        <!-- Loading State -->
        <div v-if="isLoadingHistory" class="flex flex-col items-center justify-center py-8 px-5 text-gray-500">
          <div class="ai-typing-indicator flex gap-1 items-center mb-3">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p class="m-0 text-sm text-gray-400">Loading...</p>
        </div>

        <!-- Empty State -->
        <div
          v-else-if="filteredChatHistory.length === 0"
          class="flex flex-col items-center justify-center py-10 px-5 text-gray-400"
        >
          <p class="m-0 text-sm text-gray-400">
            {{ historySearchQuery ? "No matches found" : "No chat history" }}
          </p>
        </div>

        <!-- Grouped History List -->
        <div v-else class="py-1">
          <div
            v-for="(group, groupIndex) in groupedChatHistory"
            :key="groupIndex"
            class="mb-1"
          >
            <div class="px-3 pt-1.5 pb-1 c-text-xxs font-semibold text-gray-400 capitalize bg-gray-50">{{ group.label }}</div>
            <div
              v-for="thread in group.threads"
              :key="thread.threadId"
              class="flex items-center py-1.5 px-3 cursor-pointer transition-colors duration-150 gap-2"
              :class="{
                'c-bg-brand-light': thread.threadId === threadId,
                'c-bg-brand-lightest cursor-wait pointer-events-none': loadingThreadId === thread.threadId,
                'c-hover-bg-brand-lightest': loadingThreadId !== thread.threadId,
              }"
              @click="loadChatThread(thread)"
            >
              <i class="far fa-comment text-gray-400 text-xs flex-shrink-0"></i>
              <div class="flex-1 min-w-0 flex items-center gap-1.5">
                <span
                  v-if="loadingThreadId === thread.threadId"
                  class="text-sm c-text-brand font-medium"
                  >Loading...</span
                >
                <span v-else class="ai-history-item-text m-0 c-text-tiny font-medium text-gray-800 leading-tight overflow-hidden overflow-ellipsis">{{
                  getThreadTitle(thread)
                }}</span>
                <span
                  v-if="thread.threadId === threadId"
                  class="c-bg-brand text-white c-text-xxs px-1 py-px rounded font-medium flex-shrink-0"
                  >Current</span
                >
              </div>
              <div
                class="flex items-center gap-2 flex-shrink-0"
                v-if="loadingThreadId !== thread.threadId"
              >
                <span class="text-gray-400 c-text-tiny">{{
                  formatRelativeTime(thread.timestamp || thread.createdAt)
                }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Load More Button -->
        <div
          class="py-2 px-3 text-center border-t border-gray-100"
          v-if="hasMoreHistory && !isLoadingHistory"
        >
          <button
            @click.stop="loadMoreHistory"
            class="ai-history-load-btn bg-transparent border-none c-text-brand c-text-tiny font-medium cursor-pointer py-1 px-3 rounded transition-all duration-200 c-hover-bg-brand-lightest"
            :disabled="isLoadingMoreHistory"
          >
            <span v-if="isLoadingMoreHistory">
              <i class="fas fa-spinner fa-spin"></i> Loading...
            </span>
            <span v-else>Load More</span>
          </button>
        </div>

        <!-- No More Chats Message -->
        <div
          class="py-2 px-3 text-center border-t border-gray-100"
          v-if="!hasMoreHistory && chatHistory.length > 0 && !isLoadingHistory"
        >
          <span class="text-gray-400 c-text-xxs">No more chats</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueMarkdownRender from "vue-markdown-render";
import {
  generateConversationalResponse,
  fetchChatHistory,
  fetchThreadMessages,
} from "../service/ai";

export default {
  name: "AIPromptModal",
  components: {
    VueMarkdownRender,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    generationType: {
      type: String,
      default: "method", // 'method', 'computed', 'watch', 'component', 'template'
    },
    componentContext: {
      type: Object,
      default: () => null, // Full component JSON structure
    },
    siteId: {
      type: String,
      default: null, // Site/Project ID
    },
    uri: {
      type: String,
      default: "", // Site/Project URI
    },
    globalCss: {
      type: String,
      default: "", // Global CSS for the site
    },
    hasMultiplePreviousStates: {
      type: Function,
      default: null,
    },
    revertAllPreviousStates: {
      type: Function,
      default: null,
    },
    isReverting: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      userPrompt: "",
      isGenerating: false,
      messages: [], // Array to store chat messages
      threadId: null, // Store threadId to maintain conversation context
      showHistoryPanel: false, // Control history panel visibility
      chatHistory: [], // Array to store chat history threads
      isLoadingHistory: false, // Loading state for history
      loadingThreadId: null, // Track which thread is currently being loaded
      copiedMessageIndex: null, // Track which message is currently showing "Copied" state
      isUpdatingMessageState: false, // Flag to prevent scroll during message state updates (insert, create, etc.)
      historySearchQuery: "", // Search query for filtering history
      currentChatTitleText: null, // Store the current chat title
      hasMoreHistory: false, // Pagination: whether there are more chats to load
      nextHistoryCursor: null, // Pagination: cursor for next page
      isLoadingMoreHistory: false, // Loading state for "Load More"
      forceNewThread: false, // Flag to force new thread creation on next message
    };
  },
  computed: {
    isPanelOpen() {
      return this.isOpen;
    },
    /**
     * Find the index of the latest message that has actionable buttons
     * Only the latest message's buttons should be enabled
     */
    latestActionableMessageIndex() {
      for (let i = this.messages.length - 1; i >= 0; i--) {
        const msg = this.messages[i];
        // Messages use type: "ai" for AI responses
        if (
          msg.type === "ai" &&
          msg.codeMetadata &&
          !msg.codeMetadata.isHistory
        ) {
          // Check if it has actionable content (code, components, or component creation)
          const meta = msg.codeMetadata;
          // Skip unsupported library responses (reference only, no insert buttons)
          if (meta.type === "unsupported" || meta.isReferenceOnly) {
            continue;
          }
          if (meta.type === "component" && meta.component) {
            return i; // New component creation
          }
          if (
            meta.type === "feature" &&
            meta.components &&
            meta.components.length > 0
          ) {
            return i; // Feature with components
          }
          if (meta.code || (meta.components && meta.components.length > 0)) {
            return i; // Single code or components
          }
        }
      }
      return -1;
    },
    currentChatTitleFull() {
      if (this.currentChatTitleText) {
        return this.currentChatTitleText;
      }
      if (this.messages.length > 0) {
        const firstUserMsg = this.messages.find((m) => m.type === "user");
        if (firstUserMsg && firstUserMsg.text) {
          return firstUserMsg.text;
        }
      }
      return "Your coding companion";
    },
    currentChatTitle() {
      // If we have a current chat title, show it (truncated if needed)
      if (this.currentChatTitleText) {
        const title = this.currentChatTitleText;
        return title.length > 30 ? title.substring(0, 30) + "..." : title;
      }
      // If we have messages but no title, show first user message as title
      if (this.messages.length > 0) {
        const firstUserMsg = this.messages.find((m) => m.type === "user");
        if (firstUserMsg && firstUserMsg.text) {
          const text = firstUserMsg.text;
          return text.length > 30 ? text.substring(0, 30) + "..." : text;
        }
      }
      // Default subtitle
      return "Your coding companion";
    },
    filteredChatHistory() {
      if (!this.historySearchQuery.trim()) {
        return this.chatHistory;
      }
      const query = this.historySearchQuery.toLowerCase();
      return this.chatHistory.filter((thread) => {
        const title = this.getThreadTitle(thread).toLowerCase();
        return title.includes(query);
      });
    },
    groupedChatHistory() {
      const groups = [];
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
      const threeWeeksAgo = new Date(
        today.getTime() - 21 * 24 * 60 * 60 * 1000,
      );
      const oneMonthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      const groupMap = {
        Today: [],
        Yesterday: [],
        "1w ago": [],
        "2w ago": [],
        "3w ago": [],
        "1mo ago": [],
        Older: [],
      };

      this.filteredChatHistory.forEach((thread) => {
        const threadDate = new Date(thread.timestamp || thread.createdAt);

        if (threadDate >= today) {
          groupMap["Today"].push(thread);
        } else if (threadDate >= yesterday) {
          groupMap["Yesterday"].push(thread);
        } else if (threadDate >= oneWeekAgo) {
          groupMap["1w ago"].push(thread);
        } else if (threadDate >= twoWeeksAgo) {
          groupMap["2w ago"].push(thread);
        } else if (threadDate >= threeWeeksAgo) {
          groupMap["3w ago"].push(thread);
        } else if (threadDate >= oneMonthAgo) {
          groupMap["1mo ago"].push(thread);
        } else {
          groupMap["Older"].push(thread);
        }
      });

      // Build groups array (only include non-empty groups)
      Object.keys(groupMap).forEach((label) => {
        if (groupMap[label].length > 0) {
          groups.push({ label, threads: groupMap[label] });
        }
      });

      return groups;
    },
  },
  mounted() {
    // Inject code copy buttons after initial render
    this.$nextTick(() => {
      this.injectAllCodeCopyButtons();
    });
  },
  updated() {
    // Re-inject code copy buttons after any update
    this.$nextTick(() => {
      this.injectAllCodeCopyButtons();
    });
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Focus textarea after panel opens
        this.$nextTick(() => {
          if (this.$refs.promptTextarea) {
            this.$refs.promptTextarea.focus();
          }
          // Scroll to bottom when panel opens
          this.scrollToBottom();
        });
      } else {
        // Reset messages when panel closes (optional - you can remove this if you want to keep history)
        // this.messages = [];
      }
    },
    messages: {
      handler() {
        // Skip scroll if we're just updating message state (insert, create, copy, etc.)
        if (this.isUpdatingMessageState) {
          return;
        }
        // Auto-scroll when new messages are added
        this.$nextTick(() => {
          this.scrollToBottom();
          // Inject code copy buttons into all message code blocks
          this.injectAllCodeCopyButtons();
        });
      },
      deep: true,
    },
    isGenerating(newVal) {
      // Auto-scroll when loading state changes
      if (newVal) {
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
  },
  methods: {
    /**
     * Builds a clean, structured component context for the AI
     * This provides full context including template, CSS, and complete code
     * for existing methods, computed properties, and watchers
     */
    buildComponentContext(component) {
      if (!component) {
        return null;
      }

      return {
        name: component.name || "Unnamed Component",
        summary:
          component.summary || component.description || "No summary available",
        template: component.template || component.html || "",
        globalCss: this.globalCss || "",
        existing: {
          methods:
            component.methods && Array.isArray(component.methods)
              ? component.methods
                  .map((m) => {
                    if (typeof m === "string") return { name: m, body: "" };
                    return { name: m.name || "", body: m.body || "" };
                  })
                  .filter((m) => m.name)
              : [],
          computed:
            component.computed && Array.isArray(component.computed)
              ? component.computed
                  .map((c) => {
                    if (typeof c === "string") return { name: c, body: "" };
                    return { name: c.name || "", body: c.body || "" };
                  })
                  .filter((c) => c.name)
              : [],
          watchers:
            component.watch && Array.isArray(component.watch)
              ? component.watch
                  .map((w) => {
                    if (typeof w === "string") return { name: w, body: "" };
                    return { name: w.name || "", body: w.body || "" };
                  })
                  .filter((w) => w.name)
              : [],
          props:
            component.props && Array.isArray(component.props)
              ? component.props
                  .map((p) => (typeof p === "string" ? p : p.name || p))
                  .filter(Boolean)
              : [],
        },
      };
    },
    togglePanel() {
      this.$emit("toggle");
    },
    closePanel() {
      this.$emit("close");
    },
    startNewChat() {
      // Clear all messages
      this.messages = [];
      // Reset threadId to start a new conversation thread
      this.threadId = null;
      // Clear input field
      this.userPrompt = "";
      // Reset chat title
      this.currentChatTitleText = null;
      // Set flag to force new thread on next message
      this.forceNewThread = true;
      // Focus the input field
      this.$nextTick(() => {
        if (this.$refs.promptTextarea) {
          this.$refs.promptTextarea.focus();
        }
      });
    },
    async toggleHistoryPanel() {
      this.showHistoryPanel = !this.showHistoryPanel;
      if (this.showHistoryPanel && this.chatHistory.length === 0) {
        // Fetch history when panel opens for the first time
        await this.fetchChatHistoryData();
      }
    },
    closeHistoryPanel() {
      this.showHistoryPanel = false;
    },
    async fetchChatHistoryData() {
      this.isLoadingHistory = true;
      try {
        const response = await fetchChatHistory(
          this.siteId,
          this.uri,
          20,
          null,
        );
        this.chatHistory = response.chats || [];
        this.hasMoreHistory = response.hasMore || false;
        this.nextHistoryCursor = response.nextCursor || null;
      } catch (error) {
        console.error("Error fetching chat history:", error);
        this.chatHistory = [];
        this.hasMoreHistory = false;
        this.nextHistoryCursor = null;
      } finally {
        this.isLoadingHistory = false;
      }
    },
    async loadMoreHistory() {
      if (
        !this.hasMoreHistory ||
        !this.nextHistoryCursor ||
        this.isLoadingMoreHistory
      ) {
        return;
      }

      this.isLoadingMoreHistory = true;
      try {
        const response = await fetchChatHistory(
          this.siteId,
          this.uri,
          20,
          this.nextHistoryCursor,
        );
        // Append new chats to existing list
        this.chatHistory = [...this.chatHistory, ...(response.chats || [])];
        this.hasMoreHistory = response.hasMore || false;
        this.nextHistoryCursor = response.nextCursor || null;
      } catch (error) {
        console.error("Error loading more chat history:", error);
      } finally {
        this.isLoadingMoreHistory = false;
      }
    },
    async loadChatThread(thread) {
      // Don't load if already loading or already active
      if (
        this.loadingThreadId === thread.threadId ||
        this.threadId === thread.threadId
      ) {
        return;
      }

      // Set loading state for this specific thread
      this.loadingThreadId = thread.threadId;

      // Set loading state in main chat area
      this.isGenerating = true;

      // Clear current messages while loading
      this.messages = [];

      // Store the chat title
      this.currentChatTitleText = this.getThreadTitle(thread);

      try {
        // Fetch messages for this thread
        const threadMessages = await fetchThreadMessages(
          thread.threadId,
          this.siteId,
          this.uri,
        );

        // Convert backend messages to frontend format
        this.messages = this.convertMessagesToFrontendFormat(threadMessages);

        // Set the threadId
        this.threadId = thread.threadId;

        // Close history panel after a short delay to show loading
        setTimeout(() => {
          this.closeHistoryPanel();
        }, 300);

        // Scroll to bottom
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("Error loading chat thread:", error);
        alert("Failed to load chat history. Please try again.");
      } finally {
        this.isGenerating = false;
        this.loadingThreadId = null;
      }
    },
    convertMessagesToFrontendFormat(backendMessages) {
      // Convert backend message format to frontend format
      return backendMessages.map((msg) => {
        let text = msg.content || msg.message || msg.text || "";
        let codeMetadata = null;

        // Check if metadata exists
        if (msg.metadata || msg.codeMetadata) {
          const metadata = msg.metadata || msg.codeMetadata;

          // Handle component type (new component creation)
          if (metadata.type === "component" && metadata.component) {
            // Format the text to show full component details
            let formattedText = text ? `${text}\n\n` : "";
            formattedText += `**New Component:** \`${metadata.component.name}\`\n\n`;

            if (metadata.component.summary) {
              formattedText += `**Description:** ${metadata.component.summary}\n\n`;
            }

            formattedText += `**Template:**\n\`\`\`html\n${metadata.component.template}\n\`\`\`\n\n`;

            // Format components if they exist
            if (metadata.components && metadata.components.length > 0) {
              formattedText += `**Components:**\n\n`;

              // Parse and format each component
              metadata.components.forEach((comp) => {
                const { type, name, code } = comp;

                // Skip template (already shown above)
                if (type === "template") {
                  return;
                }

                // Handle CSS with different actions
                if (type === "css") {
                  const actionLabel =
                    comp.action === "replace_all"
                      ? `**CSS Update** (Replace All)`
                      : `**CSS Update** (Append)`;
                  formattedText += `${actionLabel}\n`;
                  if (code) {
                    formattedText += `\`\`\`css\n${code}\n\`\`\`\n\n`;
                  }
                  return;
                }

                // Parse the code using the same logic as handleNewComponentResponse
                // For now, just use the code as-is, but we should parse it properly
                let displayCode = code || "";

                // For data properties, extract value if in "propertyName: value" format
                if (type === "data") {
                  const dataMatch = displayCode.match(
                    /^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s,
                  );
                  if (dataMatch) {
                    displayCode = dataMatch[2].trim();
                  }
                }

                // For methods, remove function keyword but keep async
                if (
                  type === "method" ||
                  type === "computed" ||
                  type === "watch"
                ) {
                  // Remove function keyword only
                  displayCode = displayCode.replace(/^\s*function\s+/, "");

                  // Extract function body if full function declaration (with optional async)
                  const functionMatch = displayCode.match(
                    /^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/,
                  );
                  if (functionMatch) {
                    const startIndex = functionMatch[0].length - 1;
                    let braceCount = 1;
                    let endIndex = startIndex;
                    for (
                      let i = startIndex + 1;
                      i < displayCode.length && braceCount > 0;
                      i++
                    ) {
                      if (displayCode[i] === "{") braceCount++;
                      if (displayCode[i] === "}") braceCount--;
                      if (braceCount === 0) {
                        endIndex = i;
                        break;
                      }
                    }
                    if (endIndex > startIndex) {
                      displayCode = displayCode
                        .substring(startIndex + 1, endIndex)
                        .trim();
                    }
                  }
                }

                const actionLabel =
                  comp.action === "replace" ? " (Replace Existing)" : "";
                formattedText += `**${type.charAt(0).toUpperCase() + type.slice(1)}**: \`${name}\`${actionLabel}\n\n`;
                if (displayCode) {
                  formattedText += `\`\`\`javascript\n${displayCode}\n\`\`\`\n\n`;
                }
              });
            }

            text = formattedText;

            // Check if this is a new component (not from history or already created)
            const isNewComponent =
              metadata.isNewComponent !== undefined
                ? metadata.isNewComponent
                : true;
            const isHistory = metadata.isHistory === true;

            codeMetadata = {
              type: metadata.type,
              name: metadata.name,
              component: metadata.component,
              isHistory: isHistory,
              components: (metadata.components || []).map((comp) => {
                // Parse each component code properly
                const { type, name, code, target } = comp;
                let parsedCode = code || "";

                // Parse data properties
                if (type === "data") {
                  const dataMatch = parsedCode.match(
                    /^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s,
                  );
                  if (dataMatch) {
                    parsedCode = dataMatch[2].trim();
                  }
                }

                // Parse function bodies - remove function keyword but keep async
                if (
                  type === "method" ||
                  type === "computed" ||
                  type === "watch"
                ) {
                  parsedCode = parsedCode.replace(/^\s*function\s+/, "");
                  const functionMatch = parsedCode.match(
                    /^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/,
                  );
                  if (functionMatch) {
                    const startIndex = functionMatch[0].length - 1;
                    let braceCount = 1;
                    let endIndex = startIndex;
                    for (
                      let i = startIndex + 1;
                      i < parsedCode.length && braceCount > 0;
                      i++
                    ) {
                      if (parsedCode[i] === "{") braceCount++;
                      if (parsedCode[i] === "}") braceCount--;
                      if (braceCount === 0) {
                        endIndex = i;
                        break;
                      }
                    }
                    if (endIndex > startIndex) {
                      parsedCode = parsedCode
                        .substring(startIndex + 1, endIndex)
                        .trim();
                    }
                  }
                }

                return {
                  type,
                  name,
                  code: parsedCode,
                  isNew: comp.isNew !== undefined ? comp.isNew : true,
                  action: comp.action || "add",
                  target: target || null,
                  inserted: comp.inserted || false,
                };
              }),
              isNewComponent: isNewComponent,
            };
          }
          // Handle feature type (multi-component feature)
          else if (metadata.type === "feature" && metadata.components) {
            // Format the text to show full feature details
            let formattedText = text ? `${text}\n\n` : "";
            formattedText += `**Feature:** \`${metadata.name}\`\n\n`;

            // Handle template at feature level (if present)
            if (metadata.template) {
              formattedText += `**Template:**\n\`\`\`html\n${metadata.template}\n\`\`\`\n\n`;
            }

            if (metadata.components && metadata.components.length > 0) {
              formattedText += `**Components:**\n\n`;
            }

            // Format each component
            (metadata.components || []).forEach((comp) => {
              const { type, name, code } = comp;

              // Handle template components (always complete template)
              if (type === "template") {
                formattedText += `**Template**\n`;
                if (code) {
                  formattedText += `\`\`\`html\n${code}\n\`\`\`\n\n`;
                }
                return;
              }

              // Handle CSS with different actions
              if (type === "css") {
                const actionLabel =
                  comp.action === "replace_all"
                    ? `**CSS Update** (Replace All)`
                    : `**CSS Update** (Append)`;
                formattedText += `${actionLabel}\n`;
                if (code) {
                  formattedText += `\`\`\`css\n${code}\n\`\`\`\n\n`;
                }
                return;
              }

              // Parse the code
              let displayCode = code || "";

              // For data properties
              if (type === "data") {
                const dataMatch = displayCode.match(
                  /^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s,
                );
                if (dataMatch) {
                  displayCode = dataMatch[2].trim();
                }
              }

              // For methods/computed/watch - remove function keyword but keep async
              if (
                type === "method" ||
                type === "computed" ||
                type === "watch"
              ) {
                displayCode = displayCode.replace(/^\s*function\s+/, "");
                const functionMatch = displayCode.match(
                  /^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/,
                );
                if (functionMatch) {
                  const startIndex = functionMatch[0].length - 1;
                  let braceCount = 1;
                  let endIndex = startIndex;
                  for (
                    let i = startIndex + 1;
                    i < displayCode.length && braceCount > 0;
                    i++
                  ) {
                    if (displayCode[i] === "{") braceCount++;
                    if (displayCode[i] === "}") braceCount--;
                    if (braceCount === 0) {
                      endIndex = i;
                      break;
                    }
                  }
                  if (endIndex > startIndex) {
                    displayCode = displayCode
                      .substring(startIndex + 1, endIndex)
                      .trim();
                  }
                }
              }

              const actionLabel =
                comp.action === "replace" ? " (Replace Existing)" : "";
              formattedText += `**${type.charAt(0).toUpperCase() + type.slice(1)}**: \`${name}\`${actionLabel}\n\n`;
              if (displayCode) {
                formattedText += `\`\`\`javascript\n${displayCode}\n\`\`\`\n\n`;
              }
            });

            text = formattedText;

            // Check if this is from history
            const isHistory = metadata.isHistory === true;

            // Build components array, including template at feature level if present
            const allComponents = [...(metadata.components || [])];
            if (
              metadata.template &&
              !allComponents.find((c) => c.type === "template")
            ) {
              // Add template at the beginning if not already in components
              allComponents.unshift({
                type: "template",
                name: "template",
                code: metadata.template,
                isNew: true,
              });
            }

            codeMetadata = {
              type: metadata.type,
              name: metadata.name,
              isHistory: isHistory,
              template: metadata.template || null,
              components: allComponents.map((comp) => {
                const { type, name, code, target } = comp;
                let parsedCode = code || "";

                if (type === "data") {
                  const dataMatch = parsedCode.match(
                    /^([a-zA-Z_$][a-zA-Z0-9_$]*)\s*:\s*(.+)$/s,
                  );
                  if (dataMatch) {
                    parsedCode = dataMatch[2].trim();
                  }
                }

                if (
                  type === "method" ||
                  type === "computed" ||
                  type === "watch"
                ) {
                  // Remove function keyword but keep async
                  parsedCode = parsedCode.replace(/^\s*function\s+/, "");
                  const functionMatch = parsedCode.match(
                    /^(async\s+)?([a-zA-Z_$][a-zA-Z0-9_$]*)\s*(\([^)]*\))\s*\{/,
                  );
                  if (functionMatch) {
                    const startIndex = functionMatch[0].length - 1;
                    let braceCount = 1;
                    let endIndex = startIndex;
                    for (
                      let i = startIndex + 1;
                      i < parsedCode.length && braceCount > 0;
                      i++
                    ) {
                      if (parsedCode[i] === "{") braceCount++;
                      if (parsedCode[i] === "}") braceCount--;
                      if (braceCount === 0) {
                        endIndex = i;
                        break;
                      }
                    }
                    if (endIndex > startIndex) {
                      parsedCode = parsedCode
                        .substring(startIndex + 1, endIndex)
                        .trim();
                    }
                  }
                }

                return {
                  type,
                  name,
                  code: parsedCode,
                  isNew: comp.isNew !== undefined ? comp.isNew : true,
                  action: comp.action || "add",
                  target: target || null,
                  inserted: comp.inserted || false,
                };
              }),
            };
          }
          // Handle single component (method, computed, template, etc.)
          else if (metadata.type && metadata.code) {
            codeMetadata = {
              type: metadata.type,
              name: metadata.name || "",
              code: metadata.code || "",
              action: metadata.action || "add",
              target: metadata.target || null,
              // isNew flag determines if "Insert Code" button should be shown
              // true = new/modified code, false = existing/retrieved code
              isNew: metadata.isNew !== undefined ? metadata.isNew : false,
              // isHistory flag to hide insert buttons for history messages
              isHistory: metadata.isHistory === true,
            };

            // Format code block for display (whether content exists or not)
            if (codeMetadata.type && codeMetadata.type !== "clarification") {
              // If there's existing text, append the code block to it
              let codeBlock = "";
              if (text) {
                codeBlock += "\n\n"; // Add spacing if there's existing text
              } else {
                codeBlock += `**Generated ${codeMetadata.type}:**\n\n`;
              }

              // Handle CSS with different actions
              if (codeMetadata.type === "css") {
                const actionLabel =
                  codeMetadata.action === "replace_all"
                    ? `**CSS Update** (Replace All)`
                    : `**CSS Update** (Append)`;
                codeBlock += `${actionLabel}\n\n`;
                if (codeMetadata.code) {
                  codeBlock += `\`\`\`css\n${codeMetadata.code}\n\`\`\`\n\n`;
                }
              } else {
                if (codeMetadata.name) {
                  codeBlock += `**Name:** \`${codeMetadata.name}\`\n\n`;
                }
                codeBlock += `**Code:**\n\`\`\`javascript\n${codeMetadata.code}\n\`\`\``;
              }
              text += codeBlock;
            }
          }
        }

        const frontendMsg = {
          type: msg.role === "user" ? "user" : "ai",
          text: text,
          timestamp: msg.timestamp ? new Date(msg.timestamp) : new Date(),
          useMarkdown: msg.role === "assistant" || msg.role === "ai",
          copied: false,
          inserted: false,
          componentCreated: false,
          componentCreating: false,
          codeMetadata: codeMetadata,
        };

        return frontendMsg;
      });
    },
    getThreadTitle(thread) {
      // Get title from thread (AI-generated title first, then fallback to preview)
      if (thread.title) return thread.title;
      if (thread.preview) return thread.preview;
      if (thread.firstMessage) return thread.firstMessage;
      return "Chat conversation";
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return "Just now";
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return d.toLocaleDateString();
    },
    formatRelativeTime(date) {
      if (!date) return "";
      const d = new Date(date);
      const now = new Date();
      const diffMs = now - d;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);

      if (diffMins < 1) return "now";
      if (diffMins < 60) return `${diffMins}m`;
      if (diffHours < 24) return `${diffHours}h`;

      return "";
    },
    async handleGenerate() {
      if (!this.userPrompt.trim()) return;

      const userMessage = this.userPrompt.trim();

      // Detect if this is a fresh conversation (no messages, no threadId) BEFORE adding user message
      // If so, automatically force a new thread
      const isFreshConversation =
        this.messages.length === 0 && !this.threadId && !this.forceNewThread;
      const shouldForceNewThread = this.forceNewThread || isFreshConversation;

      // Add user message to chat
      this.messages.push({
        type: "user",
        text: userMessage,
        timestamp: new Date(),
      });

      // Clear input
      this.userPrompt = "";

      // Show loading state
      this.isGenerating = true;

      // Scroll to bottom
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        // Build structured component context before calling API
        const componentContext = this.buildComponentContext(
          this.componentContext,
        );

        // Call backend API directly, passing threadId to continue conversation
        const aiResponse = await generateConversationalResponse(
          userMessage,
          componentContext,
          this.siteId,
          this.uri,
          this.threadId, // Pass existing threadId if available
          shouldForceNewThread, // Force new thread if "New Chat" was clicked OR if it's a fresh conversation
        );

        // Reset forceNewThread after first message is sent
        if (this.forceNewThread || isFreshConversation) {
          this.forceNewThread = false;
        }

        // Ensure we have a valid response
        if (!aiResponse || !aiResponse.text) {
          throw new Error("Empty response from AI");
        }

        // Store threadId from response to maintain conversation context
        if (aiResponse.threadId) {
          this.threadId = aiResponse.threadId;
        }

        // Update chat title if provided in response (backend generates title after first message)
        if (aiResponse.title) {
          this.currentChatTitleText = aiResponse.title;
        }

        // Add AI response to messages with metadata
        const aiMessage = {
          type: "ai",
          text: String(aiResponse.text),
          timestamp: new Date(),
          useMarkdown: true,
          copied: false,
          codeMetadata: aiResponse.metadata || null,
          inserted: false,
          componentCreated: false,
          componentCreating: false,
        };

        this.messages.push(aiMessage);

        // Scroll to bottom after response
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error("AI generation error:", error);

        // Add error message to chat
        this.messages.push({
          type: "ai",
          text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
          timestamp: new Date(),
          isError: true,
          copied: false,
        });

        // Emit error event
        this.$emit("error", {
          prompt: userMessage,
          error: error.message,
        });

        // Scroll to bottom after error
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } finally {
        // Always hide loading state
        setTimeout(() => {
          this.isGenerating = false;
          this.$forceUpdate();
        }, 0);
      }
    },
    /**
     * Check if code type can be inserted (method, computed, watcher, template, etc.)
     */
    isInsertableCodeType(type) {
      const insertableTypes = [
        "method",
        "computed",
        "watch",
        "watcher",
        "css",
        "data",
        "template",
      ];
      return insertableTypes.includes(type);
    },
    /**
     * Check if insert/replace button should be shown
     * Shows button if:
     * - isNew: true (new code to insert)
     * - isNew: false AND action: "replace" (existing code to replace)
     * - Template type (AI always returns complete template)
     * - CSS type (supports append, replace_all actions)
     */
    shouldShowInsertButton(metadata) {
      if (!metadata || !this.isInsertableCodeType(metadata.type)) {
        return false;
      }
      // Template type - always show button (AI always returns complete template)
      if (metadata.type === "template") {
        return true;
      }
      // CSS type - show button for all actions (append, replace_all)
      if (metadata.type === "css") {
        return true;
      }
      // Show if it's new code OR if it's a replace action
      return (
        metadata.isNew === true ||
        (metadata.isNew === false && metadata.action === "replace")
      );
    },
    /**
     * Handle reverting all previous states
     */
    handleRevertAll() {
      if (this.revertAllPreviousStates) {
        this.revertAllPreviousStates();
      }
    },
    /**
     * Handle inserting code into component
     */
    handleInsertCode(metadata, messageIndex = null) {
      const { type, name, code, action, target } = metadata;

      // Prevent scroll during state updates
      this.isUpdatingMessageState = true;

      // Disable button immediately to prevent double-clicking
      const message =
        messageIndex !== null ? this.messages[messageIndex] : null;
      if (message) {
        message.inserting = true;
        this.$forceUpdate();
      }

      // Build the event payload
      const eventPayload = {
        type: type === "watcher" ? "watch" : type, // Normalize watcher to watch
        name,
        code,
        action: action || "add", // 'add' for new, 'replace' for existing, or CSS actions
        onSuccess: () => {
          // Mark message as inserted
          const targetMessage =
            message ||
            this.messages.find(
              (m) =>
                m.codeMetadata &&
                m.codeMetadata.name === name &&
                m.codeMetadata.type === type,
            );
          if (targetMessage) {
            targetMessage.inserting = false;
            targetMessage.inserted = true;
            this.$forceUpdate();
          }
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
        onError: () => {
          if (message) {
            message.inserting = false;
            this.$forceUpdate();
          }
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
      };

      // Selector no longer needed - backend handles merging

      // Include target for template patches (legacy, not used anymore)
      if (type === "template" && target) {
        eventPayload.target = target;
      }

      // Emit event to parent (SiteEditor) to handle insertion or replacement
      this.$emit("insertCode", eventPayload);
    },
    handleInsertFeatureComponent(featureMetadata, componentIndex) {
      const component = featureMetadata.components[componentIndex];
      const { type, name, code, action, target } = component;

      // Prevent scroll during state updates
      this.isUpdatingMessageState = true;

      // Disable button immediately to prevent double-clicking
      component.inserting = true;
      this.$forceUpdate();

      // Build the event payload
      const eventPayload = {
        type: type === "watcher" ? "watch" : type,
        name,
        code,
        action: action || "add", // 'add' for new, 'replace' for existing, or CSS actions
        onSuccess: () => {
          // Mark this specific component as inserted
          component.inserting = false;
          component.inserted = true;
          this.$forceUpdate();
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
        onError: () => {
          component.inserting = false;
          this.$forceUpdate();
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
      };

      // Selector no longer needed - backend handles merging

      // Include target for template patches (legacy, not used anymore)
      if (type === "template" && target) {
        eventPayload.target = target;
      }

      // Emit event to parent (SiteEditor) to handle insertion or replacement
      this.$emit("insertCode", eventPayload);
    },
    handleCreateComponent(componentMetadata, messageIndex) {
      const { component, components } = componentMetadata;

      // Prevent scroll during state updates
      this.isUpdatingMessageState = true;

      // Disable button immediately to prevent double-clicking
      const message = this.messages[messageIndex];
      if (message) {
        message.componentCreating = true;
        this.$forceUpdate();
      }

      // Emit event to parent (SiteEditor) to handle component creation
      this.$emit("createComponent", {
        component: component,
        components: components || [],
        onSuccess: () => {
          // Mark message as component created
          if (message && message.codeMetadata) {
            message.componentCreating = false;
            message.componentCreated = true;
            // Mark all components as inserted since they were auto-inserted during creation
            if (message.codeMetadata.components) {
              message.codeMetadata.components.forEach((comp) => {
                comp.inserted = true;
              });
            }
            this.$forceUpdate();
          }
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
        onError: () => {
          // Re-enable button on error so user can try again
          if (message) {
            message.componentCreating = false;
            this.$forceUpdate();
          }
          // Re-enable scroll after updates complete
          this.$nextTick(() => {
            this.isUpdatingMessageState = false;
          });
        },
      });
    },
    scrollToBottom() {
      if (this.$refs.messageContainer) {
        this.$refs.messageContainer.scrollTop =
          this.$refs.messageContainer.scrollHeight;
      }
    },
    handleKeyDown(event) {
      // If Enter is pressed without Shift, send the message
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault(); // Prevent new line
        // Only send if there's text and not already generating
        if (this.userPrompt.trim() && !this.isGenerating) {
          this.handleGenerate();
        }
      }
      // Shift+Enter will create a new line (default behavior)
    },
    copyToClipboard(text, index) {
      if (!text) return;

      const textToCopy = String(text);

      const setCopied = (value) => {
        // Use separate tracking variable to avoid triggering messages watcher
        this.copiedMessageIndex = value ? index : null;
      };

      // Try modern Clipboard API first (doesn't cause scroll issues)
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 2000);
          })
          .catch(() => {
            // Fallback to execCommand
            this.copyWithExecCommand(textToCopy, setCopied);
          });
      } else {
        // Fallback for older browsers
        this.copyWithExecCommand(textToCopy, setCopied);
      }
    },
    injectAllCodeCopyButtons() {
      // Inject code copy buttons for all messages
      this.messages.forEach((message, index) => {
        if (message.useMarkdown && message.text) {
          this.injectCodeCopyButtons(index);
        }
      });
    },
    injectCodeCopyButtons(messageIndex) {
      // Use nextTick to ensure DOM is updated
      this.$nextTick(() => {
        const wrapper = this.$refs["markdownWrapper_" + messageIndex];
        if (!wrapper) return;

        // Handle both single element and array of elements
        const container = Array.isArray(wrapper) ? wrapper[0] : wrapper;
        if (!container) return;

        // Find all pre elements
        const preElements = container.querySelectorAll("pre");

        preElements.forEach((pre) => {
          // Skip if button already exists
          if (pre.querySelector(".ai-code-block-copy-btn")) return;

          // Get the code content before modifying
          const codeElement = pre.querySelector("code");
          const codeText = codeElement
            ? codeElement.textContent
            : pre.textContent;

          // Create a wrapper for the button that stays fixed
          const btnWrapper = document.createElement("div");
          btnWrapper.className = "ai-code-copy-wrapper";
          btnWrapper.style.position = "sticky";
          btnWrapper.style.top = "0";
          btnWrapper.style.left = "100%";
          btnWrapper.style.width = "0";
          btnWrapper.style.height = "0";
          btnWrapper.style.overflow = "visible";
          btnWrapper.style.float = "right";
          btnWrapper.style.zIndex = "10";

          // Create copy button
          const btn = document.createElement("button");
          btn.className = "ai-code-block-copy-btn";
          btn.innerHTML = '<i class="fas fa-copy" style="color: inherit;"></i>';
          btn.title = "Copy Code";

          // Apply inline styles (scoped CSS doesn't apply to dynamic elements)
          btn.style.cssText = `
            position: absolute;
            top: -8px;
            right: -6px;
            background: rgba(0, 0, 0, 0.05);
            border: none;
            border-radius: 4px;
            min-width: 20px;
            height: 20px;
            padding: 0 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: #666;
            font-size: 10px;
            transition: all 0.2s ease;
            opacity: 0;
            z-index: 10;
          `;

          // Add hover handlers for showing/hiding
          pre.addEventListener("mouseenter", () => {
            btn.style.opacity = "1";
          });
          pre.addEventListener("mouseleave", () => {
            btn.style.opacity = "0";
          });

          // Add button hover effect
          btn.addEventListener("mouseenter", () => {
            btn.style.background = "rgba(0, 0, 0, 0.1)";
            btn.style.color = "#00aeef";
          });
          btn.addEventListener("mouseleave", () => {
            btn.style.background = "rgba(0, 0, 0, 0.05)";
            btn.style.color = "#666";
          });

          // Add click handler
          btn.addEventListener("click", (e) => {
            e.stopPropagation();
            this.copyCodeBlockContent(codeText, btn);
          });

          btnWrapper.appendChild(btn);

          // Insert wrapper at the beginning of pre
          pre.insertBefore(btnWrapper, pre.firstChild);
        });
      });
    },
    copyCodeBlockContent(codeText, buttonElement) {
      if (!codeText) return;

      const textToCopy = String(codeText).trim();

      const showCopied = () => {
        buttonElement.innerHTML = '<span class="ai-copy-text">Copied</span>';
        buttonElement.classList.add("copied");
        setTimeout(() => {
          buttonElement.innerHTML = '<i class="fas fa-copy"></i>';
          buttonElement.classList.remove("copied");
        }, 2000);
      };

      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(textToCopy)
          .then(() => {
            showCopied();
          })
          .catch(() => {
            // Fallback
            this.copyCodeBlockFallback(textToCopy, showCopied);
          });
      } else {
        this.copyCodeBlockFallback(textToCopy, showCopied);
      }
    },
    copyCodeBlockFallback(text, onSuccess) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.style.opacity = "0";
      textArea.setAttribute("readonly", "");
      document.body.appendChild(textArea);
      textArea.focus({ preventScroll: true });
      textArea.select();
      try {
        if (document.execCommand("copy")) {
          onSuccess();
        }
      } catch (e) {
        console.error("Copy failed", e);
      }
      document.body.removeChild(textArea);
    },
    copyWithExecCommand(textToCopy, setCopied) {
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      textArea.style.top = "0";
      textArea.style.opacity = "0";
      textArea.style.pointerEvents = "none";
      textArea.setAttribute("readonly", "");
      textArea.setAttribute("tabindex", "-1");

      document.body.appendChild(textArea);

      // Focus without scrolling
      textArea.focus({ preventScroll: true });
      textArea.select();
      textArea.setSelectionRange(0, textToCopy.length);

      try {
        const successful = document.execCommand("copy");
        document.body.removeChild(textArea);

        if (successful) {
          setCopied(true);
          setTimeout(() => {
            setCopied(false);
          }, 2000);
        }
      } catch (err) {
        document.body.removeChild(textArea);
      }
    },
  },
};
</script>

<style scoped>
/* ========================================
   Custom utility classes (component-scoped replacements
   for tailwind.config.js custom theme values)
   ======================================== */

/* --- Brand colors --- */
.c-bg-brand { background-color: #00aeef; }
.c-bg-brand-dark { background-color: #0088cc; }
.c-bg-brand-darker { background-color: #0077a8; }
.c-bg-brand-lightest { background-color: #f0f8ff; }
.c-bg-brand-light { background-color: #e6f4ff; }
.c-bg-loading { background-color: #f0f0ff; }
.c-text-brand { color: #00aeef; }
.c-user-bubble-gradient { background: linear-gradient(to bottom right, #00aeef, #0088cc); }

/* --- Brand hover states --- */
.c-hover-bg-brand:hover { background-color: #00aeef; }
.c-hover-bg-brand-darker:hover { background-color: #0077a8; }
.c-hover-bg-brand-lightest:hover { background-color: #f0f8ff; }

/* --- Brand focus states --- */
.c-focus-border-brand:focus { border-color: #00aeef; }

/* --- Custom z-indexes --- */
.c-z-1000 { z-index: 1000; }
.c-z-99998 { z-index: 99998; }
.c-z-99999 { z-index: 99999; }

/* --- Custom font sizes --- */
.c-text-xxs { font-size: 10px; line-height: 1.4; }
.c-text-tiny { font-size: 11px; line-height: 1.4; }

/* ========================================
   Minimal CSS — only for things Tailwind can't handle:
   animations, pseudo-elements, :deep() selectors,
   parent-hover-child patterns, gradient buttons,
   and scrollbar styling.
   ======================================== */

/* --- Tooltip: parent-hover reveal + arrow pseudo-element --- */
.ai-floating-btn:hover .ai-floating-tooltip {
  opacity: 1;
}
.ai-floating-tooltip {
  position: absolute;
  right: calc(100% + 12px);
  top: 50%;
  transform: translateY(-50%);
}
.ai-floating-tooltip::after {
  content: "";
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  border: 6px solid transparent;
  border-left-color: #4a4a4a;
}

/* --- Subtitle max-width --- */
.ai-subtitle {
  max-width: 200px;
}

/* --- User message bubble: max-width + border-radius --- */
.ai-user-bubble {
  max-width: 80%;
  border-radius: 18px 18px 4px 18px;
}

/* --- AI message bubble: max-width + border-radius --- */
.ai-message-bubble-ai {
  max-width: 95%;
  border-radius: 18px 18px 18px 4px;
}

/* --- AI error bubble state --- */
.ai-error-bubble {
  background-color: #fef2f2;
  border-left: 3px solid #ef4444;
  color: #b91c1c;
}

/* --- Copy button backgrounds + interactions --- */
.ai-copy-button {
  background: rgba(0, 0, 0, 0.05);
  min-width: 20px;
}
.ai-copy-button:hover {
  background: rgba(0, 0, 0, 0.1);
  color: #00aeef;
}
.ai-copy-button:active {
  transform: scale(0.95);
}
.ai-copy-copied {
  opacity: 1 !important;
  background: rgba(76, 175, 80, 0.1) !important;
}

/* --- Copy text font size --- */
.ai-copy-text {
  font-size: 9px;
}

/* --- Panel footer upward shadow --- */
.ai-panel-footer {
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.05);
}

/* --- Prompt textarea: padding + constraints --- */
.ai-prompt-textarea {
  font-family: 'Inter', sans-serif;
  padding: 14px 58px 14px 18px;
  min-height: 56px;
  max-height: 140px;
}

/* --- History dropdown sizing --- */
.ai-history-dropdown {
  width: 280px;
  max-height: 380px;
}

/* --- History load more button disabled state --- */
.ai-history-load-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
}

/* --- Copy button always visible at end of AI reply --- */

/* --- Insert / action button (gradient + states) --- */
.ai-insert-btn {
  background: linear-gradient(135deg, #00aeef 0%, #0088cc 100%);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 174, 239, 0.2);
  white-space: nowrap;
  max-width: 100%;
}
.ai-insert-btn:hover:not([disabled]) {
  background: linear-gradient(135deg, #0088cc 0%, #006699 100%);
  box-shadow: 0 2px 6px rgba(0, 174, 239, 0.3);
  transform: translateY(-1px);
}
.ai-insert-btn:active:not([disabled]) {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(0, 174, 239, 0.25);
}
.ai-insert-btn[disabled] {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.ai-insert-btn i {
  font-size: 11px;
}
.ai-insert-btn.ai-feature-btn {
  font-size: 10px;
  padding: 4px 8px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.3;
  text-align: left;
}
.ai-insert-btn.ai-revert-btn {
  background: #f59e0b;
}
.ai-insert-btn.ai-revert-btn:hover:not([disabled]) {
  background: #d97706;
}

/* --- Send button (gradient + states) --- */
.ai-send-btn {
  background: linear-gradient(135deg, #00aeef 0%, #0088cc 100%);
  box-shadow: 0 2px 4px rgba(0, 174, 239, 0.2);
}
.ai-send-btn:hover:not([disabled]) {
  background: linear-gradient(135deg, #0088cc 0%, #006699 100%);
  box-shadow: 0 3px 8px rgba(0, 174, 239, 0.3);
  transform: translateY(-1px);
}
.ai-send-btn:active:not([disabled]) {
  transform: translateY(0);
  box-shadow: 0 1px 3px rgba(0, 174, 239, 0.25);
}
.ai-send-btn[disabled] {
  background: #e8e8e8;
  color: #b0b0b0;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

/* --- Input wrapper focus-within ring --- */
.ai-input-wrapper:focus-within {
  border-color: #00aeef;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.1);
}

/* --- Scrollbar styling for panel body --- */
.ai-panel-body {
  scrollbar-width: thin;
  scrollbar-color: #c0c0c0 #f5f5f5;
}
.ai-panel-body::-webkit-scrollbar {
  width: 6px;
}
.ai-panel-body::-webkit-scrollbar-track {
  background: #f5f5f5;
}
.ai-panel-body::-webkit-scrollbar-thumb {
  background: #c0c0c0;
  border-radius: 3px;
}
.ai-panel-body::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* --- Message slide-in animation --- */
.ai-message {
  animation: messageSlideIn 0.3s ease;
}
@keyframes messageSlideIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- Typing dots animation --- */
.ai-typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #00aeef;
  animation: typingDot 1.4s infinite ease-in-out;
}
.ai-typing-indicator span:nth-child(1) { animation-delay: 0s; }
.ai-typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.ai-typing-indicator span:nth-child(3) { animation-delay: 0.4s; }
@keyframes typingDot {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.7;
  }
  30% {
    transform: translateY(-10px);
    opacity: 1;
  }
}

/* --- History dropdown slide animation --- */
.ai-history-dropdown {
  animation: dropdownSlide 0.15s ease;
}
@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* --- History item text clamping --- */
.ai-history-item-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* --- Markdown :deep() styles for rendered content --- */
.markdown-body :deep(p) {
  margin: 0 0 10px 0;
}
.markdown-body :deep(strong) {
  font-weight: 600;
}
.markdown-body :deep(pre) {
  background-color: #f6f8fa !important;
  border-radius: 6px !important;
  padding: 12px 16px !important;
  margin: 10px 0 !important;
  overflow-x: auto !important;
  max-width: 100% !important;
  display: block !important;
  border: 1px solid #e1e4e8 !important;
  position: relative;
}
.markdown-body :deep(pre code) {
  background-color: transparent !important;
  color: #24292e !important;
  font-family: "Consolas", "Monaco", "Courier New", monospace !important;
  font-size: 12px !important;
  line-height: 1.6 !important;
  white-space: pre !important;
  display: block !important;
  word-wrap: normal !important;
  overflow-wrap: normal !important;
  padding: 0 !important;
}
.markdown-body :deep(code:not(pre code)) {
  background-color: rgba(175, 184, 193, 0.2) !important;
  color: #d63384 !important;
  padding: 2px 6px !important;
  border-radius: 4px !important;
  font-family: "Consolas", "Monaco", "Courier New", monospace !important;
  font-size: 11px !important;
  white-space: normal !important;
}
</style>
